import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/** One recorded call to the AI filter bar. */
export interface AiPromptLog {
    _id: string;
    userId: string;
    userEmail: string;
    userName?: string;
    prompt: string;
    promptKey: string;
    context: "database" | "signals";
    source: "manual" | "suggestion" | "voice";
    status: "ok" | "empty" | "error";
    errorCode?: string;
    filters?: Record<string, any>;
    filterCount: number;
    filterKeys: string[];
    unsupported: string[];
    explanation?: string;
    durationMs?: number;
    createdAt: string;
}

/** One distinct wording, with everything the fusing view needs to judge it. */
export interface AiPromptGroup {
    promptKey: string;
    prompt: string;
    calls: number;
    users: string[];
    contexts: ("database" | "signals")[];
    okCalls: number;
    emptyCalls: number;
    errorCalls: number;
    avgFilters: number;
    lastUsedAt: string;
    firstUsedAt: string;
    /** Every cluster holding this wording — a prompt can be filed under several. */
    clusters: { _id: string; name: string }[];
}

export interface AiPromptCluster {
    _id: string;
    name: string;
    note: string;
    prompts: { promptKey: string; sample: string; calls: number; lastUsedAt: string | null }[];
    calls: number;
    users: number;
    lastUsedAt: string | null;
    createdAt: string;
}

export interface AiPromptStats {
    calls: number;
    ok: number;
    empty: number;
    errors: number;
    voice: number;
    suggestion: number;
    uniquePrompts: number;
    users: number;
}

export interface AiPromptUserStat {
    email: string;
    calls: number;
    uniquePrompts: number;
    lastUsedAt: string;
}

/** Shared by every read: the same window and filters, so the page's numbers agree. */
export interface AiPromptQuery {
    days?: number | "all";
    context?: "database" | "signals" | "all";
    status?: "ok" | "empty" | "error" | "all";
    email?: string;
    q?: string;
}

const toParams = (query: AiPromptQuery = {}) => {
    const params = new URLSearchParams();
    if (query.days && query.days !== "all") params.set("days", String(query.days));
    if (query.context && query.context !== "all") params.set("context", query.context);
    if (query.status && query.status !== "all") params.set("status", query.status);
    if (query.email) params.set("email", query.email);
    if (query.q) params.set("q", query.q);
    return params;
};

export const fetchAiPromptLogs = async (query: AiPromptQuery, page = 1, limit = 50) => {
    const params = toParams(query);
    params.set("page", String(page));
    params.set("limit", String(limit));
    const { data } = await axios.get(`${API_URL}/admin/ai-prompts?${params}`, { headers: authHeaders() });
    return data as { logs: AiPromptLog[]; total: number; page: number; limit: number };
};

export const fetchAiPromptGroups = async (query: AiPromptQuery) => {
    const { data } = await axios.get(`${API_URL}/admin/ai-prompts/grouped?${toParams(query)}`, {
        headers: authHeaders(),
    });
    return data.prompts as AiPromptGroup[];
};

export const fetchAiPromptStats = async (query: AiPromptQuery) => {
    const { data } = await axios.get(`${API_URL}/admin/ai-prompts/stats?${toParams(query)}`, {
        headers: authHeaders(),
    });
    return data as { stats: AiPromptStats; byUser: AiPromptUserStat[] };
};

export const fetchAiPromptClusters = async (query: AiPromptQuery) => {
    const { data } = await axios.get(`${API_URL}/admin/ai-prompts/clusters?${toParams(query)}`, {
        headers: authHeaders(),
    });
    return data.clusters as AiPromptCluster[];
};

export const createAiPromptCluster = async (name: string, prompts: string[], note?: string) => {
    const { data } = await axios.post(
        `${API_URL}/admin/ai-prompts/clusters`,
        { name, prompts, note },
        { headers: authHeaders() }
    );
    return data.cluster;
};

export const updateAiPromptCluster = async (
    id: string,
    body: { name?: string; note?: string; addPrompts?: string[]; removePrompts?: string[] }
) => {
    const { data } = await axios.patch(`${API_URL}/admin/ai-prompts/clusters/${id}`, body, {
        headers: authHeaders(),
    });
    return data.cluster;
};

export const deleteAiPromptCluster = async (id: string) => {
    await axios.delete(`${API_URL}/admin/ai-prompts/clusters/${id}`, { headers: authHeaders() });
};

// ─── Bar suggestions ──────────────────────────────────────────────────────────

/** One opener offered when a user clicks the AI bar. */
export interface AiSuggestionRow {
    _id: string;
    prompt: string;
    context: "database" | "signals" | "both";
    order: number;
    active: boolean;
    updatedBy?: string;
    updatedAt: string;
}

export const fetchAiSuggestionRows = async () => {
    const { data } = await axios.get(`${API_URL}/admin/ai-prompts/suggestions`, { headers: authHeaders() });
    return data as { suggestions: AiSuggestionRow[]; seeded: boolean };
};

export const createAiSuggestion = async (body: Partial<AiSuggestionRow>) => {
    const { data } = await axios.post(`${API_URL}/admin/ai-prompts/suggestions`, body, { headers: authHeaders() });
    return data.suggestion as AiSuggestionRow;
};

export const updateAiSuggestion = async (id: string, body: Partial<AiSuggestionRow>) => {
    const { data } = await axios.patch(`${API_URL}/admin/ai-prompts/suggestions/${id}`, body, {
        headers: authHeaders(),
    });
    return data.suggestion as AiSuggestionRow;
};

export const deleteAiSuggestion = async (id: string) => {
    await axios.delete(`${API_URL}/admin/ai-prompts/suggestions/${id}`, { headers: authHeaders() });
};

export const reorderAiSuggestions = async (ids: string[]) => {
    await axios.post(`${API_URL}/admin/ai-prompts/suggestions/reorder`, { ids }, { headers: authHeaders() });
};

/** Re-adds the built-in openers, skipping wordings already present. */
export const seedAiSuggestions = async () => {
    const { data } = await axios.post(`${API_URL}/admin/ai-prompts/suggestions/seed`, {}, { headers: authHeaders() });
    return data.added as number;
};
